import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { ApplicationsState } from 'src/app/store/applications/applications.state';
import { ApplicationsActions } from 'src/app/store/applications/applications.action';
import { IApplications } from 'src/app/store/applications/applications.interface';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { GlobalHelperService } from 'src/app/services/global-helper.service';
import { AppCommon } from 'src/app/shared/app.common';
import { NzInputModule } from 'ng-zorro-antd/input';
import { FormsModule } from '@angular/forms';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { RouterLink } from '@angular/router';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { DatePipe, NgClass } from '@angular/common';

@Component({
	selector: 'app-applications',
	templateUrl: './applications.component.html',
	styleUrls: ['./applications.component.scss'],
	imports: [
		NzInputModule,
		FormsModule,
		NzIconModule,
		RouterLink,
		NzDropDownModule,
		NzMenuModule,
		NzPaginationModule,
		DatePipe,
		NgClass
	]
})
export class ApplicationsComponent implements OnInit {
	private readonly nzMessage = inject(NzMessageService);
	private readonly store = inject(Store);
	private readonly nzModalService = inject(NzModalService);
	private readonly globalHelperService = inject(GlobalHelperService);
	private readonly destroyRef = inject(DestroyRef);

	private readonly applicationsResult = this.store.select<any>(
		ApplicationsState.getApplications
	);
	public allApplications: IApplications[];
	public applications: IApplications[];

	public searchText = new Subject<string>();
	public searchKeyword: string;

	public currentPage = 1;
	public itemsPerPage = 25;
	public totalRows = 0;
	public paginatedData: any[] = [];
	public readonly pageSizeOption: number[] = [25, 50, 75, 100];

	constructor() {
		this.searchText
			.pipe(
				debounceTime(500),
				distinctUntilChanged(),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(() => {
				this.search();
			});
	}

	ngOnInit(): void {
		this.applicationsResult
			?.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((data) => {
				this.allApplications = data.applications;
				this.allApplications.forEach((app: any) => {
					app.isWithdrawHidden =
						app.pipelineStatus.toUpperCase() === 'APPLIED' ||
						app.pipelineStatus.toUpperCase() === 'A00 APPLIED' ||
						app.pipelineStatus.toUpperCase() === 'HIRED' ||
						app.status.toUpperCase() === 'DELETED';

					if (app.status.toUpperCase() === 'DELETED' && app.pipelineStatus.toUpperCase() !== 'HIRED') {
						app.labelChange = 'Closed';
					}else if(app.pipelineStatus.toUpperCase() === 'HIRED'){
						app.labelChange = 'Hired';
					} else if (app.status.toUpperCase().includes('CLOSED')) {
						app.labelChange = 'Closed';
					} else if (app.status.toUpperCase().includes('FAILED')) {
						app.labelChange = 'Unsuccessful Application';
					} else if (app.status.toUpperCase().includes('WITHDRAWN')) {
						app.labelChange = 'Withdrawn';
					} else if (app.status.toUpperCase() === 'SMART HIRE') {
						app.labelChange = 'Client Shortlisted' + (app.isSmartHire ? ' (Smart Hire)' : '');
					} else if (app.status.toUpperCase() !== 'DELETED' && app.pipelineStatus?.toUpperCase() !== 'HIRED') {
						app.labelChange = 'Application In-progress';
					}
				});
				this.applications = data.applications;
				this.totalRows = this.applications.length;
				this.getPaginatedData();
			});
		this.store.dispatch(new ApplicationsActions.GetApplications());
	}

	public clickJr() {
		this.nzMessage.error('This Job request is closed.');
	}

	public cancelJrApplication(jobRequest: any) {
		this.nzModalService.confirm({
			nzTitle: 'Confirmation',
			nzOkText: 'Yes',
			nzOkDanger: true,
			nzCancelText: 'No',
			nzContent: 'Are you sure you want to withdraw your application?',
			nzOnOk: () => {
				this.store.dispatch(
					new ApplicationsActions.CancelApplication(jobRequest)
				);
			}
		});
	}

	public getStatus(pipelineStatus: string, status: string): string {
		if ((status.toUpperCase() == 'DELETED' || status.toUpperCase().includes('CLOSED')) && pipelineStatus.toUpperCase() != 'HIRED') {
			return '#a2a2a2';
		}else if (pipelineStatus.toUpperCase() == 'HIRED') {
			return '#66c36a';
		} else if (status.toUpperCase() != 'DELETED'&& pipelineStatus?.toUpperCase() != 'HIRED') {
			return '#4ba9ff';
		} else {
			return '#ffad33';
		}
	}

	public getStatusStyle(label: string): { bg: string; text: string; accent: string } {
		if (!label) return { bg: '#f0f4ff', text: '#4b83cd', accent: '#4ba9ff' };
		const upper = label.toUpperCase();
		if (upper.includes('CLOSED'))
			return { bg: '#f3f3f6', text: '#6b6d7e', accent: '#a2a2a2' };
		if (upper.includes('UNSUCCESSFUL'))
			return { bg: '#fff0f0', text: '#d14343', accent: '#e25555' };
		if (upper.includes('WITHDRAWN'))
			return { bg: '#fff7ed', text: '#c27a24', accent: '#e59a30' };
		if (upper.includes('SHORTLISTED') || upper.includes('HIRED'))
			return { bg: '#eefbf0', text: '#2e8b3e', accent: '#66c36a' };
		return { bg: '#eef4ff', text: '#2b6cb0', accent: '#4ba9ff' };
	}

	public search() {
		this.applications = this.allApplications.filter(
			(application) =>
				application.postingTitle
					.toLowerCase()
					.includes(this.searchKeyword.toLowerCase()) ||
				application.pipelineStatus
					.toLowerCase()
					.includes(this.searchKeyword.toLowerCase())
		);

		this.getPaginatedData();
	}

	public pageIndexChanged(event: number): void {
		this.currentPage = event;
		this.currentPage =
			this.currentPage > 1 ? this.currentPage-- : this.currentPage++;
		this.getPaginatedData();
	}

	public pageSizeChanged(size: number): void {
		this.itemsPerPage = size;
		this.getPaginatedData();
	}

	private getPaginatedData(): void {
		this.paginatedData = this.pageData().items;
	}

	private pageData() {
		const startIndex = (this.currentPage - 1) * this.itemsPerPage;
		const endIndex = startIndex + this.itemsPerPage;
		const items = this.applications.slice(startIndex, endIndex);
		return {
			items,
			currentPage: this.currentPage,
			itemsPerPage: this.itemsPerPage,
			totalPages: this.totalRows
		};
	}

	public formatLocation(text: any) {
		return this.globalHelperService.cleanLocationText(text, ', ');
	}

	public getStatusIcon(label: string): string {
		if (!label) return 'fa-circle-info';
		const upper = label.toUpperCase();
		if (upper.includes('CLOSED')) return 'fa-circle-xmark';
		if (upper.includes('UNSUCCESSFUL')) return 'fa-circle-exclamation';
		if (upper.includes('WITHDRAWN')) return 'fa-rotate-left';
		if (upper.includes('SHORTLISTED') || upper.includes('HIRED')) return 'fa-circle-check';
		return 'fa-clock';
	}

	public getCategoryIcon(category: string): string {
		return AppCommon.getIconCategory(category);
	}
}
